import {CampaignType} from 'common/src/modules/campaigns/campaigns.types';
import ObjectStatus from 'common/src/modules/enums/static/ObjectStatus';

enum AdServingPartnerType {
  Dsp,
  Publisher,
  SSP,
  Exchange,
}

enum AiAdServingPartnerType {
  Dsp = 'DSP',
  Publisher = 'Publisher',
  SSP = 'SSP',
  Exchange = 'Exchange',
}

enum ClickUrlType {
  Pixel = 0,
  Tracker = 1, // Part of the click chain
}

enum VastVersion {
  V2_0,
  V3_0,
  V4_0,
}

enum VastVersionName {
  V2_0 = '2.0',
  V3_0 = '3.0',
  V4_0 = '4.0',
}

enum AdservingPartnerContactType {
  Main,
  Technical,
  Accounting,
}

interface RemoteAdServingPartnerData {
  id: number;
  displayName: string;
  type: AdServingPartnerType;
  status: ObjectStatus;
  description: string;
  supportedCampaignTypes: CampaignType[];
  basedOnAdservingPartner?: number;
  restrictTrackingVendors: boolean;
  doNotShareLogLevelData: boolean;
  whitelistedTrackingVendorIds: number[];
}

export enum RestringTrackingVendorsOption {
  NoApprovedVendors = 'NoApprovedVendors',
  RestrictVendors = 'RestrictVendors',
}

interface AdServingPartnerData extends Omit<RemoteAdServingPartnerData, 'type'> {
  type: string;
  basedOnAdservingPartnerName: string;
  vendorsApproval?: RestringTrackingVendorsOption;
}

interface AdservingPartnerContact {
  fullName: string;
  email: string;
  phone: string;
  type: AdservingPartnerContactType;
}

interface RemoteAdservingPartnerMacro {
  id: number;
  dspId?: number;
  name?: string;
  value: string;
  dspParameter: number;
  allowedCampaignTypes?: number[];
  allowedClients?: number[];
  isInherited?: boolean;
}

enum MacroSource {
  Parent,
  Self,
}

interface AdservingPartnerMacro extends RemoteAdservingPartnerMacro {
  macroSource: MacroSource;
}

// New API types for the split endpoints
interface RemoteAdservingPartnerMacro_UI {
  id: number;
  dspId: number;
  name?: string;
  value?: string;
  dspParameter: number;
  allowedCampaignTypes?: number[];
  allowedClients?: number[];
  isInherited: boolean;
}

interface RemoteMacros_UI {
  macros?: RemoteAdservingPartnerMacro_UI[];
}

interface AdservingPartnerMacro_UI {
  id: number;
  dspId: number;
  name: string;
  value: string;
  dspParameter: number;
  allowedCampaignTypes: number[];
  allowedClients: number[];
  isInherited: boolean;
  macroSource: MacroSource;
}

interface Macros_UI {
  macros: AdservingPartnerMacro_UI[];
}

interface RemoteAdservingPartnerWithMacros {
  dspUi: RemoteExtendedAdServingPartnerData;
  dspMacrosUi: RemoteAdservingPartnerMacro[];
}

interface AdservingPartnerWithMacros {
  partner: ExtendedAdServingPartnerData;
  macros: AdservingPartnerMacro[];
}

interface RemoteExtendedAdServingPartnerData extends AdServingPartnerData {
  key: string;
  tagName: string;
  supportedClients: number[];
  clickUrlType: ClickUrlType;
  displayRequireNoScriptSupport: boolean;
  maxCTVCampaignVariationsCount?: number | null;
  supportedVastVersions: VastVersion[];
  supportedVideoVastVersions: VastVersion[];
  supportedAudioVastVersions: VastVersion[];
  displayMaxAnimationLengthInSeconds: number;
  contacts: AdservingPartnerContact[];
  targetPlatforms: {
    olvTargetPlatformIds: number[];
    ctvTargetPlatformIds: number[];
    defaultOlvId?: number;
    defaultCtvId?: number;
  };
}

interface ExtendedAdServingPartnerData
  extends Omit<
    RemoteExtendedAdServingPartnerData,
    | 'contacts'
    | 'supportedCampaignTypes'
    | 'supportedVideoVastVersions'
    | 'supportedVastVersions'
    | 'supportedAudioVastVersions'
  > {
  mainContact?: AdservingPartnerContact;
  techContact?: AdservingPartnerContact;
  accountingContact?: AdservingPartnerContact;
  supportedCampaignTypes: CampaignType[];
  supportedVastVersions: VastVersion[];
  supportedVideoVastVersions: VastVersion[];
  supportedAudioVastVersions: VastVersion[];
  isBasedOnPartner?: boolean;
  isLimitedCTVVariations?: boolean;
}

interface RemoteSearchDspsResponse {
  dsps: RemoteAdservingPartnerWithMacros[];
  filtersExplanation: string;
}

type SearchDspsResponse = Omit<RemoteSearchDspsResponse, 'dsps'> & {
  dsps: AdservingPartnerWithMacros[];
};

// New API types for individual endpoint operations
interface RemoteAdservingPartnerInfo_UI {
  id: number;
  displayName: string | null;
  status: ObjectStatus;
  type: AdServingPartnerType;
  description: string | null;
  basedOnAdservingPartner: number | null;
  supportedCampaignTypes: CampaignType[] | null;
  clickUrlType: ClickUrlType;
  supportedClients: number[] | null;
}

interface AdservingPartnerInfo_UI {
  id: number;
  displayName: string;
  status: ObjectStatus;
  type: AdServingPartnerType;
  description: string;
  basedOnAdservingPartner: number | null;
  supportedCampaignTypes: CampaignType[];
  clickUrlType: ClickUrlType;
  supportedClients: number[];
  isBasedOnPartner: boolean;
}

// Creative Spec API types
interface RemoteTargetPlatformsData_UI {
  olvTargetPlatformIds: number[] | null;
  ctvTargetPlatformIds: number[] | null;
  defaultOlvId?: number | null;
  defaultCtvId?: number | null;
}

interface TargetPlatformsData_UI {
  olvTargetPlatformIds: number[];
  ctvTargetPlatformIds: number[];
  defaultOlvId?: number | null;
  defaultCtvId?: number | null;
}

interface RemoteCreativeSpec_UI {
  displayRequireNoScriptSupport: boolean;
  maxCTVCampaignVariationsCount: number | null;
  displayMaxAnimationLengthInSeconds: number;
  supportedVideoVastVersions: VastVersion[] | null;
  supportedAudioVastVersions: VastVersion[] | null;
  targetPlatforms: RemoteTargetPlatformsData_UI | null;
}

interface CreativeSpec_UI {
  displayRequireNoScriptSupport: boolean;
  isLimitedCTVVariations: boolean;
  maxCTVCampaignVariationsCount: number;
  displayMaxAnimationLengthInSeconds: number;
  supportedVideoVastVersions: (VastVersion | string)[];
  supportedAudioVastVersions: (VastVersion | string)[]; // Audio VAST version support
  targetPlatforms: TargetPlatformsData_UI;
}

// Data Sharing API types
interface RemoteDataSharing_UI {
  restrictTrackingVendors: boolean;
  whitelistedTrackingVendorIds: number[] | null;
}

interface DataSharing_UI {
  restrictTrackingVendors: boolean;
  whitelistedTrackingVendorIds: number[];
}

export {
  AdservingPartnerContact,
  AdservingPartnerContactType,
  AdServingPartnerData,
  AdservingPartnerInfo_UI,
  AdservingPartnerMacro,
  AdservingPartnerMacro_UI,
  AdServingPartnerType,
  AdservingPartnerWithMacros,
  AiAdServingPartnerType,
  ClickUrlType,
  CreativeSpec_UI,
  DataSharing_UI,
  ExtendedAdServingPartnerData,
  Macros_UI,
  MacroSource,
  RemoteAdServingPartnerData,
  RemoteAdservingPartnerInfo_UI,
  RemoteAdservingPartnerMacro,
  RemoteAdservingPartnerMacro_UI,
  RemoteAdservingPartnerWithMacros,
  RemoteCreativeSpec_UI,
  RemoteDataSharing_UI,
  RemoteExtendedAdServingPartnerData,
  RemoteMacros_UI,
  RemoteSearchDspsResponse,
  RemoteTargetPlatformsData_UI,
  SearchDspsResponse,
  TargetPlatformsData_UI,
  VastVersion,
  VastVersionName,
};
